import { EmbeddedCard } from "@/components/molecules/embedded-card";
import { useTranslation } from "@/features/utils/i18n";
import { cn } from "@/lib/utils";
import { StarIcon } from "@phosphor-icons/react";
import { useEffect, useState } from "react";

/**
 * The frame of the screens a visitor sees before having an account: sign-in and
 * sign-up. The form on the left, on its own, and on large screens a panel on the
 * right that says what HeyUp does and lets customers say it — a centred card on
 * an empty page was all the product had to show at the moment it is judged.
 *
 * The form keeps the card parts it already uses (`CardHeader`, `CardContent`…),
 * without the card around them.
 *
 * Embedded (`?embed`), there is no room for a showcase: the page falls back to
 * the plain card.
 */
export const AuthLayout = ({ children }: { children: React.ReactNode }) => {
  const embed = document.location.search.includes("embed");
  if (embed) return <EmbeddedCard>{children}</EmbeddedCard>;

  return (
    <div className="grid min-h-dvh bg-background lg:grid-cols-[minmax(0,1fr)_minmax(0,1fr)]">
      <div className="flex flex-col px-6 py-6 sm:px-10">
        <HeyUpLogo className="h-9" />
        <div className="flex flex-1 items-center justify-center py-10">
          <div className="w-full max-w-[26rem] animate-[fade-in_0.3s_ease-out]">
            {children}
          </div>
        </div>
      </div>
      <Showcase />
    </div>
  );
};

const HeyUpLogo = ({ className }: { className?: string }) => (
  <div className={cn("shrink-0", className)}>
    <img
      src="/logo/logo-on-white.svg"
      alt="HeyUp"
      className="h-full w-auto dark:hidden"
    />
    <img
      src="/logo/logo-on-black.svg"
      alt="HeyUp"
      className="hidden h-full w-auto dark:block"
    />
  </div>
);

/** The same five colours as the stripes of the logo, in the same order. */
const BRAND = ["#FFA415", "#F13FAA", "#C345E9", "#9076F7", "#57BCFF"];

/**
 * Customers' own words, as published on heyup.ai. Their text lives in the
 * locale files (`authShowcase.testimonials.<id>`), their identity here.
 */
const TESTIMONIALS = [
  { id: "salon", name: "Merzouk B.", color: BRAND[0] },
  { id: "rental", name: "Marc Keller", color: BRAND[4] },
  { id: "agency", name: "Camille Guerder", color: BRAND[2] },
] as const;

const ROTATE_MS = 8000;

function Showcase() {
  const { t } = useTranslation();
  const [index, setIndex] = useState(0);
  const [paused, setPaused] = useState(false);

  useEffect(() => {
    const reduced = window.matchMedia(
      "(prefers-reduced-motion: reduce)",
    ).matches;
    if (paused || reduced) return;
    const timer = setInterval(
      () => setIndex((i) => (i + 1) % TESTIMONIALS.length),
      ROTATE_MS,
    );
    return () => clearInterval(timer);
  }, [paused]);

  const current = TESTIMONIALS[index];

  return (
    <aside className="relative hidden overflow-hidden bg-[#181E2A] text-[#FFFCF5] dark:bg-[#0B0E14] dark:border-l dark:border-border lg:flex lg:flex-col lg:justify-center lg:px-14 xl:px-20">
      {/* The logo's stripes, running off the bottom corner. */}
      <div
        aria-hidden
        className="pointer-events-none absolute -bottom-48 -right-16 flex h-[480px] w-[200px] rotate-45"
      >
        {BRAND.map((color) => (
          <div
            key={color}
            className="flex-1"
            style={{ backgroundColor: color }}
          />
        ))}
      </div>

      <div className="relative z-10 max-w-lg space-y-10">
        <div className="space-y-4">
          <h2 className="text-4xl font-semibold leading-tight tracking-tight text-balance">
            {t("authShowcase.slogan")}
          </h2>
          <p className="text-lg leading-relaxed text-[#FFFCF5]/70">
            {t("authShowcase.subtitle")}
          </p>
        </div>

        <figure
          className="space-y-6 rounded-2xl bg-white/[0.06] p-7 ring-1 ring-white/10"
          onMouseEnter={() => setPaused(true)}
          onMouseLeave={() => setPaused(false)}
        >
          <div className="flex gap-1 text-[#FFA415]" aria-label="5/5">
            {Array.from({ length: 5 }).map((_, i) => (
              <StarIcon key={i} weight="fill" className="size-5" />
            ))}
          </div>
          <blockquote
            key={current.id}
            className="min-h-[7.5rem] animate-[fade-in_0.4s_ease-out] text-lg leading-relaxed"
          >
            {t("authShowcase.quoted", {
              quote: t(`authShowcase.testimonials.${current.id}.quote`),
            })}
          </blockquote>
          <figcaption className="flex items-center gap-3">
            <span
              className="flex size-11 shrink-0 items-center justify-center rounded-full text-sm font-semibold text-[#181E2A]"
              style={{ backgroundColor: current.color }}
            >
              {current.name
                .split(" ")
                .map((part) => part[0])
                .join("")}
            </span>
            <span className="min-w-0">
              <span className="block font-semibold">{current.name}</span>
              <span className="block text-sm text-[#FFFCF5]/60">
                {t(`authShowcase.testimonials.${current.id}.role`)}
              </span>
            </span>
          </figcaption>
        </figure>

        <div className="flex items-center gap-2" role="tablist">
          {TESTIMONIALS.map((item, i) => (
            <button
              key={item.id}
              type="button"
              role="tab"
              aria-selected={i === index}
              aria-label={item.name}
              onClick={() => setIndex(i)}
              className={cn(
                "h-1.5 cursor-pointer rounded-full transition-all duration-300 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#57BCFF]",
                i === index
                  ? "w-8 bg-[#FFFCF5]"
                  : "w-4 bg-[#FFFCF5]/25 hover:bg-[#FFFCF5]/45",
              )}
            />
          ))}
        </div>
      </div>
    </aside>
  );
}
